import React, { useState, useEffect } from 'react';
import { MdPerson, MdSwapHoriz, MdLocationOn, MdGroup } from 'react-icons/md';
import UserTypesChart from './UserTypesChart';
import ZoneGroupChart from './ZoneGroupChart';
import TransactionChart from './TransactionChart';
import ChartModal from '../../components/ChartModal';
import SupportChartModal from '../../components/SupportChartModal';

const DashboardView = ({ dashboardData, error, userTypesData, onNavigate, userInfo, API_BASE_URL, auth }) => {
  const [todayTransactionData, setTodayTransactionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [isSupportChartModalOpen, setIsSupportChartModalOpen] = useState(false);

  // Detect screen width for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (dashboardData.transactionDistribution) {
      setTodayTransactionData(dashboardData.transactionDistribution);
      setLoading(false);
    }
  }, [dashboardData]);

  const transactionChartData = todayTransactionData.map(item => ({
    name: item.name,
    value: item.count,
    color: item.color
  }));

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#f44336',
        backgroundColor: '#ffebee',
        borderRadius: '8px',
        border: '1px solid #ffcdd2'
      }}>
        <h3>Error Loading Dashboard</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '10px' : '0px' }}>
      {/* Regional Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: isMobile ? 15 : 20, marginBottom: '20px' }}>
        <div style={{ backgroundColor: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 10, color: '#1a1a1a' }}>
          <MdPerson size={48} style={{ color: '#36A2EB' }} />
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: 18, color: '#333' }}>Regional Users</h3>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 'bold', color: '#1976d2' }}>
              {dashboardData.usersCount || 0}
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: 12, color: '#666' }}>
              Users in your region
            </p>
          </div>
        </div>
        
        <div style={{ backgroundColor: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 10, color: '#333' }}>
          <MdSwapHoriz size={48} style={{ color: '#36A2EB' }} />
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: 18, color: '#333' }}>Regional Transactions</h3>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 'bold', color: '#1976d2' }}>
              {dashboardData.transactionsCount || 0}
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: 12, color: '#666' }}>
              Today's transactions: {todayTransactionData.reduce((sum, item) => sum + item.count, 0)}
            </p>
          </div>
        </div>

        <div style={{ backgroundColor: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 10, color: '#333' }}>
          <MdLocationOn size={48} style={{ color: '#36A2EB' }} />
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: 18, color: '#333' }}>Total Zones</h3>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 'bold', color: '#1976d2' }}>
              {dashboardData.zonesCount || 0}
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: 12, color: '#666' }}>
              Zones under management
            </p>
          </div>
        </div>
      </div>
      
      {/* Chart Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '16px 20px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: 0, color: '#2c3e50' }}>Analytics Dashboard</h3>
        <button
          onClick={() => setIsChartModalOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1565c0'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1976d2'}
        >
          📊 View Charts
        </button>
      </div>

      {/* Charts Section */}
      <div style={{ display: 'flex', gap: 20, marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 30%', minWidth: '300px' }}>
          <UserTypesChart userTypesData={userTypesData} title="Regional User Distribution" />
        </div>
        <div style={{ flex: '1 1 30%', minWidth: '300px' }}>
          {loading ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#666',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              Loading today's transactions...
            </div>
          ) : (
            <TransactionChart transactionData={transactionChartData} title="Today's Regional Transactions" />
          )}
        </div>
        <div style={{ flex: '1 1 30%', minWidth: '300px' }}>
          <ZoneGroupChart chartData={dashboardData.zoneGroupData} title="Zones & Groups Overview" />
        </div>
      </div>

      {/* Regional Statistics */}
      <div style={{ backgroundColor: '#fff', padding: 20, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#1976d2', fontSize: 20 }}>Regional Performance Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 15 }}>
          <div style={{ textAlign: 'center', padding: 15, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#28a745' }}>
              {dashboardData.transactionDistribution?.find(t => t.name === 'Successful')?.count || 0}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>Successful Transactions</div>
          </div>
          <div style={{ textAlign: 'center', padding: 15, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ffc107' }}>
              {dashboardData.transactionDistribution?.find(t => t.name === 'Pending')?.count || 0}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>Pending Transactions</div>
          </div>
          <div style={{ textAlign: 'center', padding: 15, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#dc3545' }}>
              {dashboardData.transactionDistribution?.find(t => t.name === 'Failed')?.count || 0}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>Failed Transactions</div>
          </div>
          <div style={{ textAlign: 'center', padding: 15, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#17a2b8' }}>
              {((dashboardData.transactionDistribution?.find(t => t.name === 'Successful')?.count || 0) / 
                Math.max(dashboardData.transactionsCount || 1, 1) * 100).toFixed(1)}%
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>Success Rate</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}

      {/* Chart Modal */}
      <ChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        userRole="MMDA Admin"
        userInfo={userInfo}
        dashboardData={dashboardData}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
        onSupportChartOpen={() => setIsSupportChartModalOpen(true)}
      />

      {/* Support Chart Modal */}
      <SupportChartModal
        isOpen={isSupportChartModalOpen}
        onClose={() => setIsSupportChartModalOpen(false)}
        userRole="MMDA Admin"
        userInfo={userInfo}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
      />

    </div>
  );
};

export default DashboardView;
